import React from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Span,
  Label,
  Button,
  Tooltip,
  Checkbox,
  Accordion,
  TextInput,
  InnerPanel,
  useNotification
} from '@ubisend/pulse-components';

import AllLanguagesSelect from './AllLanguagesSelect';
import CountrySelect from './CountrySelect';
import { useClientReducer } from '../reducers/index';
import { checkEmailsForTrustedSender } from '../../../api/index';

const ClientDetails = ({
  initialClient,
  onSubmit,
  loading,
  trustedSenders,
  creating = false
}) => {
  const { showError } = useNotification();
  const client = useClientReducer(initialClient);

  const handleSubmit = event => {
    event.preventDefault();

    if (!client.valid || loading) {
      return;
    }

    onSubmit(client.form);
  };

  const handleNameChange = event => {
    const name = event.target.value;

    client.dispatch({ type: client.TYPES.UPDATE_NAME, name });
  };

  const handleEmailChange = event => {
    const email = event.target.value;

    client.dispatch({ type: client.TYPES.UPDATE_EMAIL, email });
  };

  const handleLanguageIdChange = option => {
    client.dispatch({
      type: client.TYPES.UPDATE_LANGUAGE_ID,
      language_id: option.value
    });
  };

  const handlePhoneChange = event => {
    const phone = event.target.value;

    client.dispatch({ type: client.TYPES.UPDATE_PHONE, phone });
  };

  const handleAddressOneChange = event => {
    const address = event.target.value;

    client.dispatch({
      type: client.TYPES.UPDATE_ADDRESS_1,
      address_1: address
    });
  };

  const handleAddressTwoChange = event => {
    const address = event.target.value;

    client.dispatch({
      type: client.TYPES.UPDATE_ADDRESS_2,
      address_2: address
    });
  };

  const handleCityChange = event => {
    const city = event.target.value;

    client.dispatch({
      type: client.TYPES.UPDATE_CITY,
      city
    });
  };

  const handleRegionChange = event => {
    const region = event.target.value;

    client.dispatch({
      type: client.TYPES.UPDATE_REGION,
      region
    });
  };

  const handlePostcodeChange = event => {
    const postcode = event.target.value;

    client.dispatch({
      type: client.TYPES.UPDATE_POSTCODE,
      postcode
    });
  };

  const handleCountryChange = option => {
    client.dispatch({
      type: client.TYPES.UPDATE_COUNTRY,
      country: option.value
    });
  };

  const handleAuthorisationFeatureChange = event => {
    if (!event.target.checked) {
      client.dispatch({
        type: client.TYPES.REMOVE_FEATURE,
        feature: 'authorisation'
      });
    } else {
      client.dispatch({
        type: client.TYPES.ADD_FEATURE,
        feature: 'authorisation'
      });
    }
  };

  const handleWhitelabelFooterFeatureChange = event => {
    if (!event.target.checked) {
      client.dispatch({
        type: client.TYPES.REMOVE_FEATURE,
        feature: 'whitelabel footer'
      });
    } else {
      client.dispatch({
        type: client.TYPES.ADD_FEATURE,
        feature: 'whitelabel footer'
      });
    }
  };

  const handleFaqRegressionTestFeatureChange = event => {
    if (!event.target.checked) {
      client.dispatch({
        type: client.TYPES.REMOVE_FEATURE,
        feature: 'faq regression testing'
      });
    } else {
      client.dispatch({
        type: client.TYPES.ADD_FEATURE,
        feature: 'faq regression testing'
      });
    }
  };

  const handleSmallTalkFeatureChange = event => {
    if (!event.target.checked) {
      client.dispatch({
        type: client.TYPES.REMOVE_FEATURE,
        feature: 'small talk'
      });
    } else {
      client.dispatch({
        type: client.TYPES.ADD_FEATURE,
        feature: 'small talk'
      });
    }
  };

  const handleTrustedSenderChange = async (sender) => {
    const clientTrustedSendersList = client.client.client_trusted_senders;
    const isChecked = clientTrustedSendersList.some(s => s.id === sender.id);

    if (isChecked) {
      // User is trying to uncheck — validate with backend
      const hasEmails = await checkEmailsForTrustedSender(initialClient.id, sender.from_address);

      if (hasEmails.data.exists) {
        showError(`Cannot deselect ${sender.from_address} as it is currently associated with active emails.`);
        return;
      }

      // Remove trusted sender from list
      const updatedList = clientTrustedSendersList.filter(s => s.id !== sender.id);

      client.dispatch({
        type: client.TYPES.UPDATE_CLIENT_TRUSTED_SENDERS,
        client_trusted_senders: updatedList
      });
    } else {
      // Add sender to list
      const updatedList = [...clientTrustedSendersList, sender];

      client.dispatch({
        type: client.TYPES.UPDATE_CLIENT_TRUSTED_SENDERS,
        client_trusted_senders: updatedList
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <InnerPanel mb>
        <Flex col>
          <Flex col fat="true" ySpace>
            <Flex col>
              <Label htmlFor="name">Name</Label>
              <TextInput
                name="name"
                onChange={handleNameChange}
                value={client.client.name}
                id="name"
                placeholder="Joe Bloggs"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="email">Email</Label>
              <TextInput
                name="email"
                onChange={handleEmailChange}
                value={client.client.email}
                id="email"
                placeholder="joe@bloggs.com"
              />
            </Flex>
            <Flex mb col>
              <Flex mbSm center>
                <Label mb={false} htmlFor="language">
                  Default Language
                </Label>
                <Tooltip
                  tooltip={
                    <Tooltip.Content>
                      Responses and FAQs can be written in multiple languages.
                      The default will be used when building these, as well as
                      the language for new subscribers.
                    </Tooltip.Content>
                  }>
                  <Tooltip.Icon>?</Tooltip.Icon>
                </Tooltip>
              </Flex>
              <AllLanguagesSelect
                name="language"
                id="language"
                value={client.client.language_id}
                onChange={handleLanguageIdChange}
              />
            </Flex>
          </Flex>
        </Flex>
      </InnerPanel>
      <Flex mb col>
        <Accordion header="Additional Details">
          <Flex col ySpace fat>
            <Flex col>
              <Label htmlFor="phone">Phone</Label>
              <TextInput
                name="phone"
                onChange={handlePhoneChange}
                value={client.client.phone}
                id="phone"
                placeholder="012345678900"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="address_1">Address line 1</Label>
              <TextInput
                name="address_1"
                onChange={handleAddressOneChange}
                value={client.client.address_1}
                id="address_1"
                placeholder="1, A Street"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="address_2">Address line 2</Label>
              <TextInput
                name="address_2"
                onChange={handleAddressTwoChange}
                value={client.client.address_2}
                id="address_2"
                placeholder="A Place"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="city">City</Label>
              <TextInput
                name="city"
                onChange={handleCityChange}
                value={client.client.city}
                id="city"
                placeholder="London"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="region">Region</Label>
              <TextInput
                name="region"
                onChange={handleRegionChange}
                value={client.client.region}
                id="region"
                placeholder="London"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="postcode">Postcode</Label>
              <TextInput
                name="postcode"
                onChange={handlePostcodeChange}
                value={client.client.postcode}
                id="postcode"
                placeholder="SW1A 2AA"
              />
            </Flex>
            <Flex col>
              <Label htmlFor="country">Country</Label>
              <CountrySelect
                name="country"
                id="country"
                value={client.client.country}
                onChange={handleCountryChange}
              />
            </Flex>
          </Flex>
        </Accordion>
      </Flex>
      {creating ||
        (!creating &&
          (client.hasActiveFeatureGate('authorisation') ||
            client.hasActiveFeatureGate('whitelabel footer') ||
            client.hasActiveFeatureGate('faq regression testing') ||
            client.hasActiveFeatureGate('small talk'))) ? (
        <Accordion header="Opt in features">
          {creating ||
              (!creating && client.hasActiveFeatureGate('authorisation')) ? (
            <Flex xSpaceSm center between fat>
              <Checkbox
                label="Authorisation"
                checked={client.hasFeature('authorisation')}
                onChange={handleAuthorisationFeatureChange}
              />
              <Span tinyText>
                Authorise your chatbot users to restrict access to private
                conversations.
              </Span>
            </Flex>
          ) : (
            <></>
          )}
          {creating ||
              (!creating && client.hasActiveFeatureGate('whitelabel footer')) ? (
              <Flex xSpaceSm center between fat="true" mt>
              <Checkbox
                label="White labelled bot footer"
                checked={client.hasFeature('whitelabel footer')}
                onChange={handleWhitelabelFooterFeatureChange}
              />
              <Span tinyText>
                Allow this account to customise their chatbot footer.
              </Span>
            </Flex>
          ) : (
            <></>
          )}
          {creating ||
              (!creating &&
                client.hasActiveFeatureGate('faq regression testing')) ? (
              <Flex xSpaceSm center between fat="true" mt>
              <Checkbox
                label="FAQ Testing"
                checked={client.hasFeature('faq regression testing')}
                onChange={handleFaqRegressionTestFeatureChange}
              />
              <Span tinyText>
                Allow this account to access FAQ regression testing.
              </Span>
            </Flex>
          ) : (
            <></>
          )}
          {creating ||
              (!creating && client.hasActiveFeatureGate('small talk')) ? (
              <Flex xSpaceSm center between fat="true" mt>
              <Checkbox
                label="Small Talk"
                checked={client.hasFeature('small talk')}
                onChange={handleSmallTalkFeatureChange}
              />
              <Span tinyText>
                Allow this account to display Small Talk features.
              </Span>
            </Flex>
          ) : (
            <></>
          )}
        </Accordion>
      ) : (
        <></>
      )}
      <Flex mt col>
        <Accordion header="Trusted Sender"> 
          <Flex xSpaceSm center between fat="true">
            <div>
              {trustedSenders?.map((sender, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <Checkbox
                    label={sender.from_address}
                    checked={client.client.client_trusted_senders?.some(
                      clientSender => clientSender.id === sender.id
                    )}
                    onChange={() => handleTrustedSenderChange(sender)}
                  />
                </div>
              ))}
            </div>
          </Flex>
        </Accordion>
      </Flex>
      <Flex right mt>
        <Button
          type="submit"
          disabled={!client.valid}
          variant="primary"
          loading={loading}>
          Save
        </Button>
      </Flex>
    </form>
  );
};

ClientDetails.propTypes = {
  initialClient: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    language_id: PropTypes.number.isRequired,
    phone: PropTypes.string,
    address_1: PropTypes.string,
    address_2: PropTypes.string,
    city: PropTypes.string,
    region: PropTypes.string,
    postcode: PropTypes.string,
    country: PropTypes.string
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  trustedSenders: PropTypes.array.isRequired,
  creating: PropTypes.bool
};

export default ClientDetails;
